/**
 * 批处理任务管理器 - 后端化版本
 * ============================================
 *
 * 功能:
 *   1. 文本切分工具(splitTextIntoBatches / splitTextForRoster)
 *   2. Prompt 包装(buildStoryboardContent)
 *   3. AI 返回解析(parseStoryboardOutput / parseAssetOutput)
 *   4. 提交任务到后端(submitBatchTask)
 *   5. 从后端加载任务(loadBatchTaskFromServer)
 *   6. 轮询后端进度(startPolling / stopPolling)
 *   7. 把已完成批次的分镜顺序写入 script_scenes(按 batch_index 严格串行)
 *   8. 全部完成后把角色/场景/道具写入后端(_saveBatchAssetsToBackend)
 *
 * AI 推理本身由后端 Worker(BatchReasoningWorker.php)处理。
 * 前端只负责提交任务、轮询状态、解析原始 AI 输出、顺序写入 script_scenes。
 *
 * 使用方式:
 *   import {
 *     splitTextIntoBatches, submitBatchTask, loadBatchTaskFromServer,
 *     removeBatchTask, startPolling, stopPolling, retryBatchItem,
 *     getTaskProgress, BATCH_STATUS, TASK_STATUS, ROSTER_STATUS,
 *     BATCH_TRIGGER_LENGTH
 *   } from '@/utils/batchReasoningTask';
 */

import {
  createBatchTask as apiCreateBatchTask,
  getBatchTask as apiGetBatchTask,
  getBatchTaskByScript as apiGetBatchTaskByScript,
  retryBatchTaskItem as apiRetryBatchTaskItem,
  retryBatchTaskRoster as apiRetryBatchTaskRoster,
  deleteBatchTask as apiDeleteBatchTask,
} from '@/api/c/scripts';

/* ============================================
 * 常量
 * ============================================ */

/** 触发分批的字数阈值 */
export const BATCH_TRIGGER_LENGTH = 6000;

/** 每批目标字数 */
export const BATCH_TARGET_LENGTH = 3000;

/** 切分时允许的浮动字数(向前找标点) */
export const BATCH_BOUNDARY_TOLERANCE = 300;

/** 整批强制的分镜数量(尾批不限制) */
export const FULL_BATCH_SCENE_COUNT = 25;

/** 角色名册检索:每批目标字数(1万字一份) */
export const ROSTER_BATCH_TARGET_LENGTH = 10000;

/** 角色名册检索:切分浮动 */
export const ROSTER_BATCH_BOUNDARY_TOLERANCE = 500;

/** 角色名册阶段状态 */
export const ROSTER_STATUS = {
  PENDING: 'roster_pending',
  EXTRACTING: 'roster_extracting',
  DEDUPLICATING: 'roster_deduplicating',
  DONE: 'roster_done',
  FAILED: 'roster_failed',
  /** 多次卡死后后端判定为永久失败，不再自动重试 */
  STUCK_FAILED: 'roster_stuck_failed'
};

export const ROSTER_MAX_RETRIES = 3;
export const BATCH_MAX_RETRIES = 3;

/** 批次状态 */
export const BATCH_STATUS = {
  PENDING: 'pending',
  RUNNING: 'running',
  DONE: 'done',
  FAILED: 'failed'
};

/** 任务整体状态 */
export const TASK_STATUS = {
  PENDING: 'pending',
  RUNNING: 'running',
  DONE: 'done',
  FAILED: 'failed'
};

/* ============================================
 * 文本切分
 * ============================================ */

/**
 * 把长文本按规则切分成多个批次
 */
export function splitTextIntoBatches(text) {
  const cleanText = (text || '').toString();
  const total = cleanText.length;

  if (total < BATCH_TRIGGER_LENGTH) {
    return [{
      index: 0,
      text: cleanText,
      length: total,
      isTail: true,
      expectSceneCount: null,
      startSceneNo: 1,
      endSceneNo: null
    }];
  }

  const batches = [];
  let cursor = 0;
  let batchIdx = 0;
  const PUNCTUATION = ['\n', '。', '!', '?', '!', '?', '.'];

  while (cursor < total) {
    const remaining = total - cursor;

    if (remaining < BATCH_TARGET_LENGTH) {
      batches.push({
        index: batchIdx,
        text: cleanText.slice(cursor),
        length: remaining,
        isTail: true,
        expectSceneCount: null,
        startSceneNo: 0,
        endSceneNo: null
      });
      cursor = total;
      batchIdx += 1;
      break;
    }

    const targetEnd = cursor + BATCH_TARGET_LENGTH;
    const minEnd = cursor + BATCH_TARGET_LENGTH - BATCH_BOUNDARY_TOLERANCE;
    let cutAt = -1;

    for (let p = targetEnd - 1; p >= minEnd; p -= 1) {
      if (PUNCTUATION.indexOf(cleanText.charAt(p)) !== -1) {
        cutAt = p + 1;
        break;
      }
    }
    if (cutAt === -1) cutAt = targetEnd;

    const segment = cleanText.slice(cursor, cutAt);
    batches.push({
      index: batchIdx,
      text: segment,
      length: segment.length,
      isTail: false,
      expectSceneCount: FULL_BATCH_SCENE_COUNT,
      startSceneNo: 0,
      endSceneNo: 0
    });
    cursor = cutAt;
    batchIdx += 1;
  }

  let nextStart = 1;
  for (let i = 0; i < batches.length; i += 1) {
    const b = batches[i];
    b.startSceneNo = nextStart;
    if (b.expectSceneCount) {
      b.endSceneNo = nextStart + b.expectSceneCount - 1;
      nextStart = b.endSceneNo + 1;
    } else {
      b.endSceneNo = null;
    }
  }

  return batches;
}

/**
 * 把长文本按 1 万字一份切分,用于角色名册检索
 */
export function splitTextForRoster(text) {
  const cleanText = (text || '').toString();
  const total = cleanText.length;

  if (total <= ROSTER_BATCH_TARGET_LENGTH) {
    return [{ index: 0, text: cleanText, length: total }];
  }

  const batches = [];
  let cursor = 0;
  let batchIdx = 0;
  const PUNCTUATION = ['\n', '。', '!', '?', '!', '?', '.'];

  while (cursor < total) {
    const remaining = total - cursor;

    if (remaining <= ROSTER_BATCH_TARGET_LENGTH) {
      batches.push({ index: batchIdx, text: cleanText.slice(cursor), length: remaining });
      break;
    }

    const targetEnd = cursor + ROSTER_BATCH_TARGET_LENGTH;
    const minEnd = cursor + ROSTER_BATCH_TARGET_LENGTH - ROSTER_BATCH_BOUNDARY_TOLERANCE;
    let cutAt = -1;

    for (let p = targetEnd - 1; p >= minEnd; p -= 1) {
      if (PUNCTUATION.indexOf(cleanText.charAt(p)) !== -1) {
        cutAt = p + 1;
        break;
      }
    }
    if (cutAt === -1) cutAt = targetEnd;

    const segment = cleanText.slice(cursor, cutAt);
    batches.push({ index: batchIdx, text: segment, length: segment.length });
    cursor = cutAt;
    batchIdx += 1;
  }

  return batches;
}

/* ============================================
 * Prompt 包装
 * ============================================ */

/**
 * 给 step14(分镜)的输入加镜号注入头 + 可选的角色名册
 */
export function buildStoryboardContent(batch, rosterText) {
  const startNo = batch.startSceneNo;
  const endNo = batch.endSceneNo;
  const count = batch.expectSceneCount;

  let header = '';

  if (rosterText) {
    header +=
      '【角色名册(去重后)】\n' +
      '以下是本剧本的完整角色名册,在生成分镜时必须使用名册中的名称指代角色,不得自行创造或改写角色称谓。\n' +
      rosterText + '\n\n';
  }

  if (count && endNo) {
    header +=
      '【本次任务起始镜号】镜号' + startNo + '\n' +
      '【本次任务结束镜号】镜号' + endNo + '\n' +
      '【强制要求】本批次必须严格输出镜号' + startNo + '至镜号' + endNo +
        '共' + count + '个分镜,镜号必须连续,不得跳号或重复。\n' +
      '【强制要求】每个分镜的"分镜序号"字段值必须在' + startNo + '至' + endNo + '之间。\n\n' +
      '【本批次剧本正文】\n';
  } else {
    header +=
      '【本次任务起始镜号】镜号' + startNo + '\n' +
      '【强制要求】本批次镜号必须从镜号' + startNo + '开始,镜号必须连续,不得跳号或重复。\n' +
      '【说明】本批次为剧本结尾,镜数由内容自然决定,不强制数量。\n\n' +
      '【本批次剧本正文】\n';
  }
  return header + batch.text;
}

/* ============================================
 * 解析工具
 * ============================================ */

function safeParseJSON(rawText) {
  if (!rawText) return null;
  let s = String(rawText).trim();
  const cbm = s.match(/```(?:json)?\s*([\s\S]*?)```/);
  if (cbm) s = cbm[1].trim();
  try { return JSON.parse(s); } catch (e) {}
  try {
    const fixed = s.replace(/,\s*([}\]])/g, '$1');
    return JSON.parse(fixed);
  } catch (e) {}
  try {
    const lastBrace = Math.max(s.lastIndexOf('}'), s.lastIndexOf(']'));
    if (lastBrace > 0) {
      return JSON.parse(s.slice(0, lastBrace + 1).replace(/,\s*([}\]])/g, '$1'));
    }
  } catch (e) {}
  return null;
}

function parseStoryboardOutput(rawText) {
  const result = { characters: [], scenes: [], props: [], storyboards: [] };
  const obj = safeParseJSON(rawText);
  if (!obj || typeof obj !== 'object') return result;

  const charList =
    obj['人物设定清单'] || obj['characters'] || obj['人物清单'] || obj['角色清单'] || [];
  if (Array.isArray(charList)) {
    result.characters = charList.map(function (c) {
      return {
        name: c.name || c['角色名'] || c['名称'] || '',
        description: c.description || c['描述'] || c['外貌'] || JSON.stringify(c),
        raw: c
      };
    });
  }

  const sceneList =
    obj['场景清单'] || obj['scenes'] || obj['locations'] || obj['地点清单'] || [];
  if (Array.isArray(sceneList)) {
    result.scenes = sceneList.map(function (s) {
      return {
        name: s.name || s['场景名'] || s['名称'] || s['地点'] || '',
        description: s.description || s['描述'] || JSON.stringify(s),
        raw: s
      };
    });
  }

  const propList =
    obj['道具清单'] || obj['props'] || obj['核心道具清单'] || [];
  if (Array.isArray(propList)) {
    result.props = propList.map(function (p) {
      return {
        name: p.name || p['道具名'] || p['名称'] || '',
        description: p.description || p['描述'] || p.appearance || p['道具外观与细节'] || p['外观'] || '',
        raw: p
      };
    });
  }

  const sbList =
    obj['分镜脚本'] || obj['storyboards'] || obj['分镜清单'] || obj['storyboard'] || [];
  if (Array.isArray(sbList)) {
    result.storyboards = sbList.map(function (sb, i) {
      var rawIndex = sb['分镜行首'] || sb['分镜序号'] || sb.index || sb.no || '';
      var indexNum = typeof rawIndex === 'string' ? parseInt(rawIndex.replace(/[^\d]/g, ''), 10) : rawIndex;
      if (!indexNum || isNaN(indexNum)) indexNum = i + 1;

      // 解析 dialogues 数组（新格式：[{speaker, emotion, text}]）
      var dialogues = [];
      var rawDialogues = sb['dialogues'] || [];
      if (Array.isArray(rawDialogues)) {
        rawDialogues.forEach(function (d) {
          if (!d || typeof d !== 'object') return;
          dialogues.push({
            speaker: d.speaker || d['说话人'] || '旁白',
            emotion: d.emotion || d['情绪'] || '',
            text: d.text || d['文本'] || d['台词'] || ''
          });
        });
      }

      return {
        index: indexNum,
        location: sb['场景'] || sb.location || sb['地点'] || ('场景 ' + (i + 1)),
        content: sb['画面描述'] || sb.content || sb.visualPrompt || sb['内容'] || JSON.stringify(sb),
        characters: sb['角色'] || sb.characters || '',
        props: sb['道具'] || sb.props || '',
        narration: sb['旁白文本'] || sb['旁白'] || sb.narration || '',
        dialogue: sb['角色对白'] || sb.dialogue || '',
        shotType: sb['景别与角度'] || sb.shotType || '',
        sfx: sb['音效与BGM'] || sb['音效'] || sb.sfx || '',
        mood: sb['画面情绪基调'] || sb['情绪基调'] || sb.mood || '',
        shotHeader: sb['分镜行首'] || '',
        timePoint: sb['叙事时间点'] || sb.timePoint || '',
        duration: sb['时长估算'] || sb.duration || '',
        transition: sb['转场方式'] || sb.transition || '',
        dialogues: dialogues.length > 0 ? dialogues : null,
        raw: sb
      };
    });
  }

  return result;
}

function parseAssetOutput(rawText) {
  const r = parseStoryboardOutput(rawText);
  return { characters: r.characters, scenes: r.scenes, props: r.props };
}

/* ============================================
 * 处理新完成的批次（只做本地解析缓存，供 UI 展示）
 * 分镜/角色/场景/道具的写入已迁移至后端 BatchReasoningWorker，前端不再调用 HTTP 接口
 * ============================================ */

/**
 * 扫描已完成批次，解析 raw AI 响应并缓存到内存中（供 UI 展示角色/分镜列表）。
 * 不做任何后端 HTTP 调用。
 */
async function _processNewlyDoneItems(backendTask) {
  const taskId = backendTask.taskId;

  if (!_localItemCache[taskId]) _localItemCache[taskId] = {};
  const cache = _localItemCache[taskId];

  const items = (backendTask.items || []).slice().sort(function (a, b) {
    return a.batchIndex - b.batchIndex;
  });

  for (var i = 0; i < items.length; i++) {
    var item = items[i];

    if (item.status !== 'done') break; // 未完成，后续也跳过

    if (cache[item.batchIndex]) continue; // 已解析缓存

    if (!item.rawStoryboardResp) continue;

    // 解析 AI 原始输出，仅用于前端 UI 展示
    var parsed = parseStoryboardOutput(item.rawStoryboardResp);
    var characters = parsed.characters;
    var scenes     = parsed.scenes;
    var props      = parsed.props;

    if (item.rawAssetResp) {
      var assets = parseAssetOutput(item.rawAssetResp);
      if (assets.characters.length > 0) characters = assets.characters;
      if (assets.scenes.length > 0)     scenes     = assets.scenes;
      if (assets.props.length > 0)      props      = assets.props;
    }

    cache[item.batchIndex] = {
      storyboards: parsed.storyboards,
      characters:  characters,
      scenes:      scenes,
      props:       props
    };
  }
}

export function getTaskProgress(task) {
  if (!task || !task.batches) return { done: 0, total: 0, failed: 0, running: 0, percent: 0 };
  const total = task.batches.length;
  let done = 0, failed = 0, running = 0;
  task.batches.forEach(function (b) {
    if (b.status === BATCH_STATUS.DONE) done += 1;
    else if (b.status === BATCH_STATUS.FAILED) failed += 1;
    else if (b.status === BATCH_STATUS.RUNNING) running += 1;
  });
  return {
    total, done, failed, running,
    percent: total > 0 ? Math.round((done / total) * 100) : 0
  };
}

/* ============================================
 * 内存缓存
 * ============================================
 *
 * _localItemCache: 每个 taskId 下已解析的批次数据(storyboards/chars/scenes/props)。
 *   页面刷新后丢失,重新从后端拉 raw AI 响应解析即可。
 *
 * 批次是否已写入 script_scenes 由后端 item.scenesSaved 字段决定，前端不再维护本地副本。
 */

const _localItemCache = {};   // taskId -> { batchIndex -> { storyboards, characters, scenes, props } }
const _pollTimers    = {};    // taskId -> intervalId
const _pollRunning   = {};    // taskId -> bool (防止同一任务的 poll 并发重入)

/* ============================================
 * 格式转换:后端 → 前端
 * ============================================ */

/**
 * 把后端返回的任务格式转换为前端 UI 所需的格式。
 * 已解析的数据(storyboards/characters/scenes/props)从 _localItemCache 读取。
 */
function _adaptBackendTask(backendTask) {
  if (!backendTask) return null;
  const taskId = backendTask.taskId;
  const cache = _localItemCache[taskId] || {};

  const batches = (backendTask.items || []).map(function (item) {
    const localData = cache[item.batchIndex];
    return {
      index: item.batchIndex,
      batchItemDbId: item.id || null,
      status: item.status,
      startSceneNo: item.startSceneNo,
      endSceneNo: item.endSceneNo,
      expectSceneCount: item.expectSceneCount,
      isTail: item.isTail,
      length: item.inputLength || 0,
      storyboardRaw: item.rawStoryboardResp || '',
      assetRaw: item.rawAssetResp || '',
      error: item.errorMessage || item.failureReason || '',
      retryCount: item.retryCount || 0,
      savedToBackend: !!item.scenesSaved,
      storyboards: localData ? localData.storyboards : [],
      characters: localData ? localData.characters : [],
      scenes:     localData ? localData.scenes : [],
      props:      localData ? localData.props : []
    };
  });

  return {
    id:           backendTask.id || null,
    taskId:       backendTask.taskId,
    scriptId:     backendTask.scriptId,
    scriptName:   backendTask.scriptName,
    status:       backendTask.status,
    commentaryMode: backendTask.commentaryMode,
    styleName:    backendTask.styleName,
    aspectRatio:  backendTask.aspectRatio,
    modelIds:     backendTask.modelIds || {},
    totalBatches: backendTask.totalBatches,
    doneBatches:  backendTask.doneBatches,
    failedBatches: backendTask.failedBatches,
    rosterStatus: backendTask.rosterStatus,
    rosterText:   backendTask.rosterText || '',
    rosterError:  backendTask.rosterError || '',
    createdAt:    backendTask.createdAt,
    updatedAt:    backendTask.updatedAt,
    batches:      batches
  };
}

/* ============================================
 * 公开 API
 * ============================================ */

/**
 * 提交分批推理任务到后端
 *
 * @param {object} opts
 * @param {string|number} opts.scriptId
 * @param {string} opts.scriptName
 * @param {string} opts.fullText
 * @param {string} opts.styleName
 * @param {string} opts.aspectRatio
 * @param {boolean} opts.commentaryMode
 * @param {boolean} opts.isVip
 * @param {object} opts.modelIds
 * @returns {Promise<object>} 前端格式的任务对象
 */
export async function submitBatchTask(opts) {
  const resp = await apiCreateBatchTask({
    scriptId:      opts.scriptId,
    scriptName:    opts.scriptName || '',
    fullText:      opts.fullText   || '',
    styleName:     opts.styleName  || '',
    aspectRatio:   opts.aspectRatio || '9:16',
    commentaryMode: opts.commentaryMode ? 1 : 0,
    is_vip:        opts.isVip ? true : false,
    modelIds:      opts.modelIds || {}
  });
  // 后端可能返回"已有进行中的任务",直接使用
  return _adaptBackendTask(resp.data);
}

/**
 * 从服务端加载该 scriptId 下最新的任务(用于页面加载时恢复)
 *
 * @param {string|number} scriptId
 * @returns {Promise<object|null>}
 */
export async function loadBatchTaskFromServer(scriptId) {
  const resp = await apiGetBatchTaskByScript(scriptId);
  if (!resp.data) return null;
  // 预填缓存：解析 done 批次的原始 AI 响应，供工作台 UI 直接渲染
  await _processNewlyDoneItems(resp.data);
  return _adaptBackendTask(resp.data);
}

/**
 * 删除任务(向后端发起删除请求 + 清理本地缓存)
 *
 * @param {string} taskId
 */
export async function removeBatchTask(taskId) {
  await apiDeleteBatchTask(taskId);
  delete _localItemCache[taskId];
  delete _pollTimers[taskId];
  delete _pollRunning[taskId];
}

/**
 * 重试某个失败的批次
 *
 * @param {string} taskId
 * @param {number} batchIndex
 */
export async function retryBatchItem(taskId, batchIndex) {
  await apiRetryBatchTaskItem(taskId, batchIndex);
  // 清除该批次在本地缓存中的状态,让 poll 重新处理
  if (_localItemCache[taskId]) {
    delete _localItemCache[taskId][batchIndex];
  }
}

/**
 * 重试角色名册阶段(后端 Worker 会自动重跑检索+去重)
 *
 * @param {string} taskId
 */
export async function retryBatchRoster(taskId) {
  await apiRetryBatchTaskRoster(taskId);
}

/**
 * 启动轮询,定期从后端拉取任务状态并更新 UI。
 *
 * 轮询逻辑:
 *   1. 立即执行一次
 *   2. 每 3 秒执行一次
 *   3. 检测到已完成批次 → 解析 AI 原始输出 → 顺序写入 script_scenes
 *   4. 任务 status=done → 写入角色/场景/道具资产 → 调用 onDone
 *   5. 任务 status=failed → 调用 onDone(让用户决定是否重试)
 *
 * @param {string} taskId 后端任务 id
 * @param {string|number} scriptId 用于写入 script_scenes
 * @param {function} onUpdate 每次状态变化回调 (task) => void
 * @param {function} onDone 任务终止(done/failed)回调 (task) => void
 */
export function startPolling(taskId, onUpdate, onDone) {
  // 清除已有的轮询(如重试时重启)
  if (_pollTimers[taskId]) {
    clearInterval(_pollTimers[taskId]);
    delete _pollTimers[taskId];
  }

  async function _poll() {
    // 防止并发重入
    if (_pollRunning[taskId]) return;
    _pollRunning[taskId] = true;

    try {
      var resp = await apiGetBatchTask(taskId);
      var backendTask = resp && resp.data;
      if (!backendTask) return;

      // 解析已完成批次的 raw AI 响应（仅供 UI 展示，数据库写入由后端 Worker 负责）
      await _processNewlyDoneItems(backendTask);

      // 构建前端格式的任务对象
      var task = _adaptBackendTask(backendTask);
      if (onUpdate) onUpdate(task);

      // 检查任务是否终止
      var isTaskDone = backendTask.status === 'done' || backendTask.status === 'failed';
      // 名册永久失败也视为终止（批次永远无法开始推理）
      var isRosterStuckFailed = backendTask.rosterStatus === ROSTER_STATUS.STUCK_FAILED;
      if (isTaskDone || isRosterStuckFailed) {
        clearInterval(_pollTimers[taskId]);
        delete _pollTimers[taskId];
        if (onDone) onDone(task);
      }
    } catch (e) {
      console.warn('[batchTask] 轮询异常:', e && e.message);
    } finally {
      _pollRunning[taskId] = false;
    }
  }

  // 立即执行一次,随后每 3 秒一次
  _poll();
  _pollTimers[taskId] = setInterval(_poll, 3000);
}

/**
 * 停止轮询
 *
 * @param {string} taskId
 */
export function stopPolling(taskId) {
  if (_pollTimers[taskId]) {
    clearInterval(_pollTimers[taskId]);
    delete _pollTimers[taskId];
  }
  _pollRunning[taskId] = false;
}
